import React from "react";

import type { OverlayInteractionEvent, PreparedPolygon } from "../types";
import { polygonStyle } from "../styles";

interface PolygonRendererProps {
  annotation: PreparedPolygon;
  /** Display-pixel vertex positions already mapped through the video layout. */
  points: readonly { x: number; y: number }[];
  containerWidth: number;
  containerHeight: number;
  onInteraction: (event: OverlayInteractionEvent) => void;
}

/**
 * Renders a polygon annotation using an absolutely-positioned SVG that spans
 * the entire overlay container. We use absolute coordinates inside the SVG
 * (no viewBox transform) so the polygon lands at the same display pixels the
 * box renderer would use — this keeps hover hit-testing straightforward.
 */
export const PolygonRenderer: React.FC<PolygonRendererProps> = React.memo(
  ({ annotation, points, containerWidth, containerHeight, onInteraction }) => {
    if (points.length < 3) return null;

    const style = polygonStyle;
    const pointsAttr = points.map((p) => `${p.x},${p.y}`).join(" ");
    const hoverId = annotation.interactionId ?? annotation.id;
    const clickId = annotation.id;

    const svgStyle: React.CSSProperties = {
      position: "absolute",
      left: 0,
      top: 0,
      width: containerWidth,
      height: containerHeight,
      pointerEvents: "none",
      overflow: "visible",
    };

    return (
      <svg data-annotation-id={clickId} style={svgStyle}>
        <polygon
          fill={style.fill}
          points={pointsAttr}
          stroke={style.stroke}
          strokeWidth={style.strokeWidth}
          style={{ pointerEvents: "auto", cursor: "pointer" }}
          onClick={() => onInteraction({ type: "click", annotationId: clickId })}
          onMouseEnter={() => onInteraction({ type: "mouseenter", annotationId: hoverId })}
          onMouseLeave={() => onInteraction({ type: "mouseleave", annotationId: hoverId })}
        />
      </svg>
    );
  },
);

PolygonRenderer.displayName = "PolygonRenderer";
